import { useEffect, useRef, useState } from "react";
import type { PlannedDrill } from "../data/schema";
import { BlockTag, Button } from "./ui";
import { isVideoApproved } from "../lib/overrides";

const BLOCK_LABEL: Record<string, string> = {
  warmup: "Warm-up",
  technical: "Technical",
  position: "Focus",
  challenge: "Challenge",
  cooldown: "Cooldown",
};

function fmt(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export function DrillCard({
  pd,
  index,
  done,
  onToggleDone,
  onSwap,
}: {
  pd: PlannedDrill;
  index: number;
  done: boolean;
  onToggleDone: () => void;
  onSwap: () => void;
}) {
  const d = pd.drill;
  const [open, setOpen] = useState(index === 0);
  const [showVideo, setShowVideo] = useState(false);
  const [left, setLeft] = useState(pd.allocatedMin * 60);
  const [running, setRunning] = useState(false);
  const ref = useRef<number | null>(null);

  useEffect(() => {
    if (!running) return;
    ref.current = window.setInterval(() => {
      setLeft((l) => {
        if (l <= 1) {
          setRunning(false);
          return 0;
        }
        return l - 1;
      });
    }, 1000);
    return () => {
      if (ref.current) window.clearInterval(ref.current);
    };
  }, [running]);

  const approved = isVideoApproved(d.id, d.video.verified);
  const hasVideo = d.video.youtubeId.length > 0;

  return (
    <div className={`card overflow-hidden ${done ? "opacity-60" : ""}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--color-paper)] font-bold">
          {done ? "✓" : index + 1}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <BlockTag label={BLOCK_LABEL[pd.block] ?? pd.block} />
            <span className="text-sm font-semibold text-neutral-500">
              {pd.allocatedMin} min
            </span>
          </span>
          <span className="mt-1 block truncate text-xl font-bold">{d.name}</span>
        </span>
      </button>

      {open && (
        <div className="border-t border-[var(--color-line)] px-4 py-4">
          {d.setupNeed !== "solo" && (
            <p className="mb-2 inline-block rounded-md bg-amber-100 px-2 py-1 text-sm font-semibold text-amber-800">
              Needs {d.setupNeed === "server" ? "a server" : "a partner"}
            </p>
          )}
          <p className="mb-3 text-base text-neutral-700">{d.setup}</p>

          <ol className="mb-3 list-decimal space-y-1 pl-5 text-base">
            {d.steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>

          {d.cues.length > 0 && (
            <div className="mb-3 rounded-xl bg-[var(--color-paper)] p-3">
              <p className="h-display text-lg text-[var(--color-pitch-dark)]">Coaching cues</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-base">
                {d.cues.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          {(d.variants.easier || d.variants.harder) && (
            <p className="mb-3 text-sm text-neutral-600">
              {d.variants.easier && (
                <>
                  <b>Easier:</b> {d.variants.easier}{" "}
                </>
              )}
              {d.variants.harder && (
                <>
                  <b>Harder:</b> {d.variants.harder}
                </>
              )}
            </p>
          )}

          {/* video */}
          {hasVideo ? (
            showVideo ? (
              <div>
                <div className="relative w-full overflow-hidden rounded-xl" style={{ paddingTop: "56.25%" }}>
                  <iframe
                    className="absolute inset-0 h-full w-full"
                    src={`https://www.youtube-nocookie.com/embed/${d.video.youtubeId}?start=${d.video.startSec ?? 0}&rel=0`}
                    title={d.video.title}
                    allow="accelerometer; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                {!approved && (
                  <p className="mt-1 text-sm text-amber-700">
                    Candidate video — not yet approved. Check it matches the drill.
                  </p>
                )}
              </div>
            ) : (
              <Button variant="ghost" className="w-full" onClick={() => setShowVideo(true)}>
                ▶ Watch demo{approved ? "" : " (unverified)"}
              </Button>
            )
          ) : (
            <p className="text-sm italic text-neutral-500">No video yet for this drill.</p>
          )}

          {/* timer + controls */}
          <div className="mt-4 flex items-center gap-2">
            <span className="h-display w-24 text-4xl tabular-nums">{fmt(left)}</span>
            <Button variant={running ? "amber" : "primary"} onClick={() => setRunning((r) => !r)}>
              {running ? "Pause" : "Start"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setRunning(false);
                setLeft(pd.allocatedMin * 60);
              }}
            >
              Reset
            </Button>
          </div>

          <div className="mt-3 flex gap-2">
            <Button variant={done ? "amber" : "ghost"} className="flex-1" onClick={onToggleDone}>
              {done ? "Done ✓" : "Mark done"}
            </Button>
            <Button variant="ghost" onClick={onSwap}>
              Swap
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
